import { createRootRoute, Outlet } from '@tanstack/react-router';
import { useEffect } from 'react';

import { useAuthState } from '../modules/auth/ui/auth-state-context';

const SESSION_IDLE_TIMEOUT_MS = 8 * 60 * 60 * 1000;
const ACTIVITY_EVENTS = ['pointerdown', 'keydown', 'touchstart', 'scroll', 'focus'] as const;

export const Route = createRootRoute({
  component: () => (
    <div className="min-h-screen">
      <Outlet />
      <IdleSessionTimer />
    </div>
  ),
});

// Headless: expires the session after SESSION_IDLE_TIMEOUT_MS of inactivity.
// Expiring only clears the auth store (sessionExpired=true, session=null);
// the actual navigation + user-facing notice are handled reactively by
// useRedirectWhenSignedOut (authenticated layout) and the login page's
// sessionExpired banner, respectively — see use-redirect-on-sign-out.ts and
// LoginPage.tsx.
function IdleSessionTimer() {
  const { session, sessionExpired, expireSession } = useAuthState();

  useEffect(() => {
    if (!session || sessionExpired) return;

    let timeoutId: number | undefined;
    let lastActivityAt = Date.now();

    function scheduleExpiration() {
      if (timeoutId) window.clearTimeout(timeoutId);
      const elapsed = Date.now() - lastActivityAt;
      const remaining = Math.max(0, SESSION_IDLE_TIMEOUT_MS - elapsed);
      timeoutId = window.setTimeout(() => {
        void expireSession();
      }, remaining);
    }

    function recordActivity() {
      const elapsed = Date.now() - lastActivityAt;
      if (elapsed >= SESSION_IDLE_TIMEOUT_MS) {
        void expireSession();
        return;
      }
      lastActivityAt = Date.now();
      scheduleExpiration();
    }

    for (const eventName of ACTIVITY_EVENTS) {
      window.addEventListener(eventName, recordActivity, { passive: true });
    }
    scheduleExpiration();

    return () => {
      if (timeoutId) window.clearTimeout(timeoutId);
      for (const eventName of ACTIVITY_EVENTS) {
        window.removeEventListener(eventName, recordActivity);
      }
    };
  }, [session, sessionExpired, expireSession]);

  return null;
}
